import React, { useState, useRef, useEffect } from 'react';
import { chatService } from '../lib/chatService';
import { chatPrompts } from '../lib/chatPrompts';
import { chatConversationService } from '../lib/chatConversationService';
import { useAuth } from '../contexts/AuthContext';
import { ChatHistory } from './ChatHistory';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CustomerSupportChatProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CustomerSupportChat: React.FC<CustomerSupportChatProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: chatPrompts.welcomeMessage,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const [messageOrder, setMessageOrder] = useState(1);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      
      // Start a new conversation when chat opens
      if (user?.id && !conversationId) {
        const newSessionId = chatConversationService.generateSessionId();
        
        chatConversationService.startConversation(user.id, newSessionId)
          .then(convId => {
            setConversationId(convId);
            console.log('Started conversation:', convId);
          })
          .catch(error => {
            console.error('Failed to start conversation:', error);
          });
      }
      
      // Test the API connection when chat opens
      chatService.testConnection().then(isConnected => {
        if (!isConnected) {
          console.warn('OpenRouter API connection test failed');
        }
      });
    }
  }, [isOpen, user?.id, conversationId]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !user?.id || !conversationId) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Save user message to database
    try {
      await chatConversationService.addMessage(
        conversationId,
        user.id,
        'user',
        inputMessage,
        messageOrder
      );
      setMessageOrder(prev => prev + 1);
    } catch (error) {
      console.error('Failed to save user message:', error);
    }

    try {
      const messagesToSend = [
        {
          role: 'system' as const,
          content: chatService.getSystemPrompt()
        },
        ...messages.map(msg => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content
        })),
        {
          role: 'user' as const,
          content: inputMessage
        }
      ];

      const startTime = Date.now();
      const response = await chatService.sendMessage(messagesToSend);
      const responseTime = Date.now() - startTime;
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save AI response to database
      try {
        await chatConversationService.addMessage(
          conversationId,
          user.id,
          'assistant',
          response,
          messageOrder,
          false, // isFallback
          'google/gemma-3-27b-it', // modelUsed
          responseTime, // responseTimeMs
          undefined // tokensUsed
        );
        setMessageOrder(prev => prev + 1);
      } catch (error) {
        console.error('Failed to save AI response:', error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Provide helpful fallback responses based on common keywords
      const userMessageLower = inputMessage.toLowerCase();
      let fallbackResponse = chatPrompts.fallbackResponses.default;
      
      if (userMessageLower.includes('token') || userMessageLower.includes('credit')) {
        fallbackResponse = chatPrompts.fallbackResponses.tokens;
      } else if (userMessageLower.includes('moodboard') || userMessageLower.includes('board')) {
        fallbackResponse = chatPrompts.fallbackResponses.moodboard;
      } else if (userMessageLower.includes('touchup') || userMessageLower.includes('edit')) {
        fallbackResponse = chatPrompts.fallbackResponses.touchup;
      } else if (userMessageLower.includes('upstaging') || userMessageLower.includes('transform')) {
        fallbackResponse = chatPrompts.fallbackResponses.upstaging;
      } else if (userMessageLower.includes('gallery') || userMessageLower.includes('image')) {
        fallbackResponse = chatPrompts.fallbackResponses.gallery;
      } else if (userMessageLower.includes('payment') || userMessageLower.includes('buy')) {
        fallbackResponse = chatPrompts.fallbackResponses.payment;
      }
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: fallbackResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      // Save fallback response to database
      try {
        await chatConversationService.addMessage(
          conversationId,
          user.id,
          'assistant',
          fallbackResponse,
          messageOrder,
          true, // isFallback
          undefined, // modelUsed
          undefined, // responseTimeMs
          undefined // tokensUsed
        );
        setMessageOrder(prev => prev + 1);
      } catch (error) {
        console.error('Failed to save fallback response:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = async () => {
    // End the conversation when chat is closed
    if (conversationId && user?.id) {
      try {
        await chatConversationService.endConversation(conversationId);
        console.log('Ended conversation:', conversationId);
      } catch (error) {
        console.error('Failed to end conversation:', error);
      }
    }
    
    // Reset conversation state
    setConversationId(null);
    setMessageOrder(1);
    setMessages([{
      id: '1',
      role: 'assistant',
      content: chatPrompts.welcomeMessage,
      timestamp: new Date()
    }]);
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">AI</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">RenovirAI Support</h3>
              <p className="text-xs text-gray-500">AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHistory(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              title="View Chat History"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                <div className={`text-xs mt-1 ${
                  message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-4 py-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex space-x-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={1}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Chat History Modal */}
      <ChatHistory 
        isOpen={showHistory} 
        onClose={() => setShowHistory(false)} 
      />
    </div>
  );
}; 